import { BookingTabItem } from 'components/Bookings2/Navbar/BookingTabItem';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import classes from 'components/Bookings2/Navbar/Navbar.module.scss';
import { BookingTab, BookingTabId, getBookingTabs } from 'components/Bookings2/Navbar/bookingTabs';
import {
    getBookingTabStorageKey,
    readBookingTabOrder,
    reconcileBookingTabOrder,
    saveBookingTabOrder
} from 'components/Bookings2/Navbar/bookingTabStorage';
import useAuth from 'hooks/useAuth';
import useServiceCategories from 'hooks/useServiceCategories';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

export const BookingsNavbar: React.FC = () => {
    const auth = useAuth();
    const location = useLocation();
    const {
        hasGrooming,
        hasMobileGrooming,
        hasDental,
        hasDaycare,
        hasBoarding,
        hasEvaluation,
        hasTransportation,
        hasTraining,
        hasWalking
    } = useServiceCategories();
    const [orderedIds, setOrderedIds] = useState<Array<BookingTabId>>([]);
    const orderedIdsRef = useRef<Array<BookingTabId>>([]);
    const storageKey = getBookingTabStorageKey(auth.user?.id, auth.marketplaceId);
    const tabs = useMemo(
        () =>
            getBookingTabs({
                hasGrooming,
                hasMobileGrooming,
                hasDental,
                hasDaycare,
                hasBoarding,
                hasEvaluation,
                hasTransportation,
                hasTraining,
                hasWalking
            }),
        [
            hasBoarding,
            hasDaycare,
            hasDental,
            hasEvaluation,
            hasGrooming,
            hasMobileGrooming,
            hasTraining,
            hasTransportation,
            hasWalking,
            location.pathname
        ]
    );
    const availableIds = useMemo(() => tabs.map(tab => tab.id), [tabs]);
    const visibleTabs = useMemo(() => {
        const reconciledIds = reconcileBookingTabOrder(orderedIds, availableIds);
        return reconciledIds
            .map(id => tabs.find(tab => tab.id === id))
            .filter((tab): tab is BookingTab => Boolean(tab?.visible));
    }, [availableIds, orderedIds, tabs]);

    useEffect(() => {
        const savedOrder = readBookingTabOrder(storageKey, availableIds);
        orderedIdsRef.current = savedOrder;
        setOrderedIds(savedOrder);
    }, [availableIds, storageKey]);

    const handleTabMove = useCallback(
        (sourceId: BookingTabId, targetId: BookingTabId) => {
            const reconciledOrder = reconcileBookingTabOrder(orderedIdsRef.current, availableIds);
            const visibleIds = reconciledOrder.filter(id => tabs.find(tab => tab.id === id)?.visible);
            const sourceIndex = visibleIds.indexOf(sourceId);
            const targetIndex = visibleIds.indexOf(targetId);

            if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return;

            const reorderedVisibleIds = reorder({
                list: visibleIds,
                startIndex: sourceIndex,
                finishIndex: targetIndex
            });

            const visibleIdSet = new Set(reorderedVisibleIds);
            const hiddenIds = reconciledOrder.filter(id => !visibleIdSet.has(id));
            const nextOrder = [...reorderedVisibleIds, ...hiddenIds];

            orderedIdsRef.current = nextOrder;
            setOrderedIds(nextOrder);
        },
        [availableIds, tabs]
    );

    const handleTabDrop = useCallback(() => {
        saveBookingTabOrder(storageKey, orderedIdsRef.current);
    }, [storageKey]);

    return (
        <div className={classes.root}>
            <p className={classes.title}>Your Bookings</p>

            <nav className={classes.menu}>
                {visibleTabs.map(tab => (
                    <BookingTabItem
                        key={tab.id}
                        tab={tab}
                        onMove={handleTabMove}
                        onDrop={handleTabDrop}
                    />
                ))}
            </nav>
        </div>
    );
};
